import React from "react"
import { useTranslation } from "react-i18next"
import { useId } from "@reach/auto-id"

import { isNotEmpty } from "utils/validation"
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input"

import { handleInternalInputValidation } from "../formUtils"
import { Errors } from "../Errors"

import "react-phone-number-input/style.css"
import "./InputPhone.scoped.scss"

export const InputPhone = ({
	// Attributes
	id,
	value = "",
	name,
	label = "",
	placeholder = "06 00 00 00 00",
	unit = "",
	disabled,
	maxlength = 14,
	// Styles
	style,
	defaultCountry = "FR",
	country,
	className = "",
	classNameLabel = "",
	classNameInput = "",
	// Error management
	required = false,
	customRules,
	showErrors,
	isResetField,
	// Handler
	onChange,
	onValidate,
	...rest
}) => {
	const [phone, setPhone] = React.useState(value)
	const prefix = useId()
	const inputId = id ?? `${prefix}-${name}`
	const errorId = `${inputId}-error`

	React.useEffect(() => {
		// TODO: get country code + add maxLength
		if (value) setPhone(value)
	}, [value]) // eslint-disable-line

	React.useEffect(() => {
		if (isResetField) {
			setPhone(null)
		}
	}, [isResetField]) // eslint-disable-line

	const inputRef = React.useRef(null)
	const [errors, setErrors] = React.useState([])
	const { t } = useTranslation()

	const rules = React.useMemo(
		() => [
			{
				key: "input-empty",
				check: (value) => (required ? isNotEmpty(value) : true),
				message: t("error_mandatory_field", { ns: "form" }),
			},
			{
				key: "valid-value",
				check: (value) => {
					// No need to check this field if empty value + not required
					if (!required && !isNotEmpty(value)) return true
					return isValidPhoneNumber(value)
				},
				message: t("error_invalid_phone_number", { ns: "form" }),
			},
			...(customRules || []),
		],
		[required, t]
	) // eslint-disable-line
	// customRules in dependencies array trigger an infinite loop,
	// because custom rules include a check property defining a new function,
	// preventing useMemo to return a valid shallow-comparison
	// or event useDeepMemo to return a valid deep-comparison

	const handleChange = (value) => {
		const inputValue = {
			target: {
				type: "phone",
				name: name,
				value,
				//country: country?.countryCode
			},
		}
		onChange(inputValue)
		setPhone(value)
	}

	const validate = React.useCallback(
		(currentValue) =>
			handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
		[rules, name, onValidate]
	)

	// validate on mount and each time value change
	React.useEffect(() => {
		validate(value)
		return () => {
			// On unmounted erros and validation are no longer needed
			setErrors([])
			if (onValidate) onValidate(name, [])
		}
	}, [value, validate, onValidate, name])

	// Custom input to manage maxLength
	// ------------------------
	const CustomInput = React.useMemo(() => {
		return React.forwardRef((props, ref) => (
			<input ref={ref} maxLength={maxlength} {...props} />
		))
	}, [maxlength])

	return (
		<>
			<div
				data-cy={`formfield-${name ?? ""}`}
				className={"c-form-group " + className}
				style={style}
			>
				<label
					data-cy={`label-${name ?? ""}`}
					className={"c-label mb-1 " + classNameLabel}
					htmlFor={inputId}
				>
					{label} {required && "*"}
				</label>

				<div data-cy={`input-${name ?? ""}`} className="flex items-baseline">
					<PhoneInput
						ref={inputRef}
						className={
							"c-input " +
							(showErrors && errors.length > 0 ? "has-error " : " ") +
							(classNameInput ? classNameInput : "")
						}
						id={inputId}
						name={name}
						placeholder={placeholder}
						value={phone}
						defaultCountry={defaultCountry}
						country={defaultCountry}
						international={false}
						disabled={disabled}
						required={required}
						onChange={(value) => !disabled && handleChange(value)}
						inputComponent={CustomInput}
						aria-describedby={errorId}
						aria-invalid={Boolean(errors.length)}
					/>
					{unit && <span className="ml-2">{unit}</span>}
				</div>

				{/* Error messages */}
				{showErrors && <Errors dataCy={`error-${name}`} id={errorId} errors={errors} />}
			</div>
		</>
	)
}
